/**
 * The three landing pages, as data.
 *
 * Design package "Alchemiq landing pages", v1.0, 20 Oct 2026:
 * `HANDOFF_Journalist_Landing_Page.md` plus the two variant READMEs. Its rule
 * is "one template, three configs" and that is literally this file: every
 * string below is transcribed from the `.dc.html` sources, which are the
 * authority on copy, and `LandingPageV3.jsx` is the template they feed.
 *
 * Only copy, feed categories, chips and form mode differ between the three.
 * Anything that is the same on all three pages belongs in the template, not
 * here, so that a shared element cannot drift between pages.
 */

import { CAMPAIGN_SEND_WINDOW_LABEL, CAMPAIGN_SEND_WINDOW_SENTENCE } from './sendWindow';

/** The eight chips the journalist and marketing pages share. Max two picked. */
const TRIAL_TOPICS = [
  'Health', 'Science', 'Tourism', 'Defense',
  'Geopolitics', 'Business & economy', 'Technology & AI', 'Climate & environment',
];

/**
 * Display chip to the category slug `GET /api/stories` understands.
 *
 * The example feed card is specified as real stories filtered by the selected
 * category, not the prototype's "Sample headline" text, so every category the
 * card offers has to resolve to something the stories API accepts. The slugs
 * come from `categoryMap` in `backend/app.js`.
 *
 * TWO OF THESE ARE APPROXIMATIONS and are flagged in the plan as needing a
 * product decision: we have no Defense category, and no Consumer & lifestyle.
 * They are mapped to the nearest real one rather than left unmapped, because
 * an unmapped category renders an empty card on a page that says "Detected by
 * Alchemiq". When the decision lands, Defense is expected to become a keyword
 * run rather than a category.
 */
export const CATEGORY_SLUGS = {
  Health: 'health',
  Science: 'science',
  Tourism: 'travel',
  Defense: 'politics',
  Geopolitics: 'politics',
  'Business & economy': 'business',
  'Technology & AI': 'technology',
  'Climate & environment': 'environment',
  'Health & science': 'health',
  'Consumer & lifestyle': 'wellness',
  Travel: 'travel',
  Entertainment: 'entertainment',
  'World politics': 'politics',
};

/** Categories whose mapping above is a stand-in, not a real equivalent. */
export const APPROXIMATE_CATEGORIES = ['Defense', 'Consumer & lifestyle'];

const SHARED_FAQ_SOURCES = [
  'Where do the stories come from?',
  'From news publishers worldwide. Each story links to its sources.',
];

const JOURNALIST = {
  audience: 'journalist',
  campaign: 'journalist',
  page: 'landing_v3',
  mode: 'trial',
  /*
   * The header link out to the marketing site.
   *
   * Per-audience because the mobile handoff sends the marketing page somewhere
   * else, and because it had been hardcoded in the template, so every page
   * pointed at the homepage regardless. `websiteLabelPhone` is set only where
   * the label has to shorten to fit.
   */
  websiteUrl: 'https://alchemiq.ai/',
  websiteLabel: 'Alchemiq website',
  heroPill: 'Free trial',
  h1: ['Find the story ', 'that matters.'],
  intro: 'Alchemiq helps to expand news coverage. It reads global news in real time, across '
    + 'languages, and turns it into stories for your beat, with sources and links.',
  noteBold: 'Get a feed like this in your inbox.',
  noteRest: ' Pick a category, add your email, and your first list arrives in minutes.',
  noteCta: 'Get my feed \u2192',
  faqHeading: 'Want to know more?',
  faqHeadingSize: 26,
  feedCategories: ['Health', 'Science', 'Defense', 'Geopolitics', 'Tourism'],
  topics: TRIAL_TOPICS,
  topicsLabel: 'Let us know which topics interest you',
  freeTextLabel: 'Or tell us what you want to find',
  freeTextPlaceholder:
    'e.g. new research on sleep and memory, with a focus on studies from the last year',
  faq: [
    ['What is Alchemiq?',
      'A news intelligence platform. It reads global news in real time, across languages, '
      + 'and turns it into stories with sources and links.'],
    ['What do I get in the free trial?',
      // Was "arrives every weekday at 8:00 CET" only. The immediate first send
      // shipped 22 Oct 2026, so the first list is now minutes away and saying
      // otherwise undersells the thing the page was built to deliver. The hour
      // comes from ./sendWindow so copy and schedule cannot drift again.
      'A free trial. Your first list of stories is emailed to you within minutes of '
      + `signing up, and after that your daily update arrives ${CAMPAIGN_SEND_WINDOW_SENTENCE}.`],
    ['Do I need a credit card?',
      'No. You won’t be asked for a credit card to begin the trial. You can unsubscribe at '
      + 'any time.'],
    ['Do I need an account?',
      'No. Enter your email, choose your topics and the first stories are sent to you.'],
    SHARED_FAQ_SOURCES,
  ],
};

const MARKETING = {
  ...JOURNALIST,
  audience: 'marketing',
  campaign: 'marketing',
  page: 'landing_v3_marketing',
  /*
   * Marketing has no `noteBold`/`noteRest`, deliberately: its hero carries no
   * note box. The label is still needed, because the jump-to-form button
   * renders on its own when there is no box to sit in.
   */
  noteCta: 'Get my feed \u2192',
  // The one page whose header link does not go to the homepage. Required by
  // the mobile handoff's acceptance list; it had been pointing at the homepage
  // like the other two because the URL was hardcoded in the template.
  websiteUrl: 'https://alchemiq.ai/solutions/pr',
  h1: ['Lead the conversation. ', 'Find stories that grow your brand.'],
  intro: 'Alchemiq is an RTM machine for PR, content, and marketing teams. It analyzes '
    + 'global news across countries and languages and uncovers timely stories, trends, and '
    + 'opportunities for media pitching, thought leadership, and content, with sources and links.',
  introBold: 'RTM machine for PR, content, and marketing teams',
  topicsLabel: 'What do you want Alchemiq to find for you?',
  freeTextLabel: 'Or describe your brand, clients or markets',
  freeTextPlaceholder:
    'e.g. product launches in consumer health, with angles our CEO can comment on, in the US and UK',
  faq: [
    ['What is Alchemiq?',
      'A news intelligence platform for PR, content and marketing teams. It analyzes global '
      + 'news across countries and languages and surfaces timely stories, trends and '
      + 'opportunities, with sources and links.'],
    ['What do I get in the free trial?',
      // "The first one arrives today" was removed in Oct 2026 because there
      // was no immediate send. There is one now, so the claim is restored, 
      // as "within minutes", which is what the code actually does, and held
      // back rather than sent when the first list is too thin to be worth it.
      'A free trial. Your first list is emailed to you within minutes of signing up: '
      + 'media hooks, thought-leadership angles, content ideas and trends. After that '
      + `your daily update arrives ${CAMPAIGN_SEND_WINDOW_SENTENCE}.`],
    ['Do I need a credit card?',
      'No. You won’t be asked for a credit card to begin the trial. You can unsubscribe at '
      + 'any time.'],
    ['Can I follow several brands or clients?',
      'Yes. Describe them in the free-text box, or write to us and we will set up a more '
      + 'focused brief for each.'],
    ['Do I need an account?',
      'No. Enter your email, choose what you want to find and the first opportunities are '
      + 'sent to you.'],
    SHARED_FAQ_SOURCES,
  ],
};

const NEWSROOM = {
  audience: 'newsroom',
  campaign: 'newsroom',
  page: 'landing_v3_newsroom',
  /*
   * Was `'demo'`, changed 29 October 2026 by Liad's decision after Adi found
   * the newsroom form completely dead.
   *
   * The demo branch posted to the TRIAL endpoint with `consent: false`, which
   * the server rejects with `CAMPAIGN_NO_CONSENT` before writing anything, and
   * the page reported success regardless. It also dropped the name and
   * publication it collected. Production held zero newsroom visits and zero
   * demo requests after two submissions.
   *
   * Newsroom is now an ordinary trial page: same form, same consent, same feed.
   * It keeps its own topic set and its own copy.
   *
   * `campaignDemoService` and the `CampaignDemoRequest` model are deliberately
   * untouched, because the older `/c/` funnel still uses them.
   */
  mode: 'trial',
  websiteUrl: 'https://alchemiq.ai/',
  websiteLabel: 'Alchemiq website',
  // Shortened on a phone. This header used to carry an orange "Request a demo"
  // button too, so all three items had to share one line at 360px. The button
  // went with the demo mode, but the short label still reads better small.
  websiteLabelPhone: 'alchemiq.ai',
  heroPill: 'News intelligence for newsrooms',
  h1: ['Find what matters. ', 'Grow your audience.'],
  intro: 'Alchemiq is a news media intelligence platform that surfaces trending stories '
    + 'across languages and countries before anyone else and creates AI drafts for your '
    + 'editing team.',
  noteBold: 'See what your newsroom could be covering.',
  noteRest: ' Pick a category to preview the stories Alchemiq surfaces and get them by email.',
  noteCta: 'Get story opportunities \u2192',
  // The prototype's note box ends "Click any story to experience the full flow,
  // up to an AI-generated draft." The variant README marks that flow as NOT
  // built and says to remove the sentence if it cannot ship. It has not
  // shipped, so the sentence is not rendered: the page must not promise an
  // interaction that does nothing.
  proof: [
    ['150,000+', 'News stories analyzed every day'],
    ['TOP 5–10', 'Alchemiq-sourced stories regularly rank among clients’ top-performing stories'],
    ['83%', 'Average traffic growth'],
  ],
  faqHeading: 'Q&A',
  faqHeadingSize: 28,
  // The newsroom story row carries a "Why it matters:" line between headline
  // and sources, and drops the source line to 12px. The line is rendered only
  // when a story actually has that text: `GET /api/stories` returns
  // title/publishedAt/sources/articles and nothing of the kind, and the design
  // package's own rule for this field is "omit if the API has no such field".
  // Inventing a reason under a strip captioned "Detected by Alchemiq" is the
  // one thing this page must never do. Open item 4 in the handoff covers it.
  showWhyItMatters: true,
  feedCategories: ['Business & economy', 'Technology & AI', 'Health & science', 'Consumer & lifestyle'],
  topics: [
    'Business & economy', 'Technology & AI', 'Health & science', 'Consumer & lifestyle',
    'Travel', 'Entertainment', 'Climate & environment', 'World politics',
  ],
  topicsLabel: 'Which topics matter most to your newsroom?',
  freeTextLabel: 'Anything you’d like us to focus on? (optional)',
  freeTextPlaceholder: '',
  trialTopicsLabel: 'Let us know which topics interest you',
  trialFreeTextLabel: 'Or tell us what you want to find',
  faq: [
    ['What is Alchemiq?',
      'A real-time global news intelligence platform. It surfaces trending stories across '
      + 'languages and countries and shows editorial teams what is gaining momentum and may '
      + 'matter to their audience.'],
    ['What do I get?',
      'A daily list of story opportunities chosen for the topics you pick, each one showing '
      + 'the original reporting behind it so your desk can judge it quickly.'],
    ['Who is Alchemiq for?',
      'Editors and newsroom decision makers who want relevant stories across beats, markets '
      + 'and languages, beyond the sources they already monitor.'],
    ['What does it cost to start?',
      'Nothing. You won’t be asked for a credit card to begin the trial. Your first list '
      + `arrives within minutes, and after that your daily update arrives ${CAMPAIGN_SEND_WINDOW_SENTENCE}.`],
    ['Can I log in to Alchemiq and create stories there?',
      'Yes. Newsroom teams get access to the Alchemiq platform, where they can explore '
      + 'detected stories and generate news content that follows attribution rules. Reply to '
      + 'any of your daily emails and we will show you how this works for your newsroom.'],
    SHARED_FAQ_SOURCES,
  ],
};

export const LANDING_CONFIGS = {
  journalist: JOURNALIST,
  marketing: MARKETING,
  newsroom: NEWSROOM,
};

/** Which audiences have a v3 landing page. `publisher` deliberately does not. */
export const V3_AUDIENCES = Object.keys(LANDING_CONFIGS);

export function landingConfigFor(audience) {
  return LANDING_CONFIGS[audience] || null;
}
